import { useCallback, useMemo } from 'react';
import { useToast } from '../components/ui/Toast';

export type ShareTarget = 'native' | 'twitter' | 'facebook' | 'whatsapp' | 'telegram' | 'copy';

export interface UseShareOptions {
  url: string;
  title: string;
  text?: string;
  image?: string;
}

export interface UseShareReturn {
  share: (target: ShareTarget) => Promise<void>;
  copyLink: () => Promise<boolean>;
  canNativeShare: boolean;
  shareTargets: ShareTarget[];
}

/**
 * Build a shareable URL from origin + path
 * Uses VITE_SHARE_ORIGIN if set. On production Filmple hosts, fall back to
 * share.filmple.io so social previews hit the worker OG unfurl endpoint.
 */
export function buildShareUrl(path: string): string {
  const configuredOrigin = import.meta.env.VITE_SHARE_ORIGIN;
  if (configuredOrigin) {
    return `${configuredOrigin}${path}`;
  }

  const { origin, hostname } = window.location;
  const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';

  if (isLocalHost) {
    return `${origin}${path}`;
  }

  if (hostname === 'filmple.io' || hostname.endsWith('.filmple.io')) {
    return `https://share.filmple.io${path}`;
  }

  return `${origin}${path}`;
}

/**
 * Build share text in format: "{title} — Filmple"
 */
export function buildShareText(title: string): string {
  return `${title} — Filmple`;
}

/**
 * Hook for sharing content to various platforms
 */
export function useShare(options: UseShareOptions): UseShareReturn {
  const { toast } = useToast();
  const { url, title, text } = options;

  const shareText = text ?? buildShareText(title);

  const canNativeShare = useMemo(() => {
    return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  }, []);

  const shareTargets = useMemo<ShareTarget[]>(() => {
    const targets: ShareTarget[] = ['copy', 'twitter', 'facebook', 'whatsapp', 'telegram'];
    if (canNativeShare) {
      targets.push('native');
    }
    return targets;
  }, [canNativeShare]);

  const copyLink = useCallback(async (): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch {
      // Fallback for older browsers
      try {
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        textarea.style.pointerEvents = 'none';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        return true;
      } catch {
        return false;
      }
    }
  }, [url]);

  const share = useCallback(async (target: ShareTarget): Promise<void> => {
    const encodedUrl = encodeURIComponent(url);
    const encodedText = encodeURIComponent(shareText);

    switch (target) {
      case 'copy': {
        const success = await copyLink();
        if (success) {
          toast.success('Link copied!');
        } else {
          toast.error('Failed to copy link');
        }
        break;
      }

      case 'twitter': {
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
        window.open(twitterUrl, '_blank', 'noopener,noreferrer');
        break;
      }

      case 'facebook': {
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        window.open(facebookUrl, '_blank', 'noopener,noreferrer');
        break;
      }

      case 'whatsapp': {
        const whatsappUrl = `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
        break;
      }

      case 'telegram': {
        const telegramUrl = `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;
        window.open(telegramUrl, '_blank', 'noopener,noreferrer');
        break;
      }

      case 'native': {
        if (canNativeShare) {
          try {
            await navigator.share({
              title,
              text: shareText,
              url,
            });
          } catch (err) {
            // User cancelled or share failed - silently ignore per spec
            if (err instanceof Error && err.name !== 'AbortError') {
              console.error('Native share failed:', err);
            }
          }
        }
        break;
      }
    }
  }, [url, title, shareText, copyLink, canNativeShare, toast]);

  return {
    share,
    copyLink,
    canNativeShare,
    shareTargets,
  };
}
